'use client';
import { useEffect, useRef } from 'react';
import { useMessageEmitter } from '../react/eventEmitterContext';
import { useSubscription } from '@apollo/client';
import { gql } from '@apollo/client';
import { Client } from './client';
import { isMicroapp } from '@utils/general';
import { getLogger } from '@utils/general';
import { loadConfig, getConfig } from '@utils/config';

const log = getLogger('utils.graphql.setup');

export const client = Client.singleton();

if (isMicroapp() && window.parent.location.origin !== 'localhost') {
  client.setup(`${window.parent.location.origin}/api/ssot`);
} else {
  console.warn('Setting SSoT URL from config - this should only be used in development');
  let config;
  loadConfig('/config.json').then(() => {
    config = getConfig();
    client.setup(config.BASE_URL || '');
  }).catch((error) => {
    console.error('Failed to load config on App startup:', error);
  });
}

export const MAILBOX_SUBSCRIPTION = gql`
  subscription onMessage {
    mailbox {
    sender
    payload {
      action
      model
      data
    }
  }
}`;

export const SUB_CHANNEL_SUBSCRIPTION = gql`
  subscription onMessage {
    sub_channel {
    message_id,
    channel,
    message,
    data,
    timestamp
  }
}`;

function useHandleSubscription(subscription, onData, onError) {
  // Use refs so Apollo's onData/onError callbacks don't become stale closures
  // when the caller passes new inline arrow functions, without needing to be
  // recreated (which would cause useSubscription to resubscribe).
  const onDataRef = useRef(onData);
  useEffect(() => { onDataRef.current = onData; }, [onData]);
  const onErrorRef = useRef(onError);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  useSubscription(subscription, {
    client: client.client,
    onComplete: () => log.info('Subscription established.'),
    shouldResubscribe: true,
    onData: ({ data }) => {
      if (data?.data) onDataRef.current(data.data);
    },
    onError: (err) => {
      log.warn('Subscription setup failed. Retrying...');
      log.error('Subscription error: ', err);
      if (onErrorRef.current) onErrorRef.current(err);
    },
  });
}

export function useWebSocketHandler() {
  const messenger = useMessageEmitter();

  useHandleSubscription(MAILBOX_SUBSCRIPTION,
    (data) => messenger.emit(data.mailbox),
    (error) => log.error('Mailbox subscription error: ', error)
  );

  useHandleSubscription(SUB_CHANNEL_SUBSCRIPTION,
    (data) => messenger.emit(data.sub_channel),
    (error) => log.error('Subchannel subscription error: ', error)
  );

  return null;
}